class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();
    setTimeout(() => this.setHeaderHeight(), 0);
    setTimeout(() => this.setHeaderHeight(), 50);
    setTimeout(() => this.setHeaderHeight(), 100);

    const barObserver = new MutationObserver(() => {
      this.setHeaderHeight();
    });
    const observeBar = (el) => {
      if (el) barObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class'], childList: false, subtree: false });
    };
    observeBar(document.querySelector('.blackfriday-urgency-bar'));
    observeBar(document.getElementById('shipping-announcement-bar'));

    this._onBarShown = () => {
      this.setHeaderHeight();
      requestAnimationFrame(() => this.setHeaderHeight());
      setTimeout(() => this.setHeaderHeight(), 50);
    };
    window.addEventListener('shipping-announcement-bar:shown', this._onBarShown);

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));
    window.matchMedia('(max-width: 749px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.setHeaderHeight(), 0);
      });
    }

    window.addEventListener('load', () => {
      setTimeout(() => this.setHeaderHeight(), 100);
    });

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => this.preventReveal = true;

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    if (!this.header) return;

    const headerHeight = this.header.offsetHeight || 80;
    document.documentElement.style.setProperty('--header-height', headerHeight + 'px');

    const shippingBar = document.getElementById('shipping-announcement-bar');
    let shippingBarHeight = (shippingBar && shippingBar.classList.contains('show') && getComputedStyle(shippingBar).display !== 'none')
      ? shippingBar.offsetHeight
      : 0;
    if (window.innerWidth <= 749 && shippingBar && shippingBarHeight === 0) {
      shippingBarHeight = 40;
    }
    const urgencyBar = document.querySelector('.blackfriday-urgency-bar');
    const urgencyBarHeight = urgencyBar ? urgencyBar.offsetHeight : 0;
    const announcementTotalHeight = Math.max(shippingBarHeight + urgencyBarHeight, window.innerWidth <= 749 ? 40 : 0);

    document.documentElement.style.setProperty('--announcement-bar-height', announcementTotalHeight + 'px');

    if (window.innerWidth <= 749) {
      const totalHeight = announcementTotalHeight + headerHeight;
      this.header.style.setProperty('top', announcementTotalHeight + 'px', 'important');
      document.body.style.paddingTop = totalHeight + 'px';
      document.body.style.setProperty('padding-top', totalHeight + 'px', 'important');
    } else {
      document.body.style.paddingTop = '';
      document.body.style.removeProperty('padding-top');
      this.header.style.top = '';
    }
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
    window.removeEventListener('shipping-announcement-bar:shown', this._onBarShown);
  }

  createObserver() {
    const observer = new IntersectionObserver((entries) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });
    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const isMobile = window.innerWidth <= 749;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (isMobile) {
      this.header.classList.add('shopify-section-header-sticky');
      const hadCompact = this.header.classList.contains('header-mobile-compact');
      if (scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header', 'header-mobile-compact');
      } else {
        this.header.classList.remove('scrolled-past-header', 'header-mobile-compact');
      }
      const hasCompact = this.header.classList.contains('header-mobile-compact');
      if (hadCompact !== hasCompact) {
        requestAnimationFrame(() => this.setHeaderHeight());
      }
      this.currentScrollTop = scrollTop;
      return;
    }

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);
        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    if (this.searchModal) this.searchModal.close(false);
  }
}

customElements.define('sticky-header', StickyHeader);
